import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PageHeader, DashboardCard, FilterBar, StatusBadge,
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from '@zafroi/ui-kit';
import { useProposals } from '@/hooks/useCommercial';
import { useCustomerNameMap } from '@/hooks/useKosh';
import { QueryStateGuard } from '@/components/common/QueryStateGuard';
import { proposalStatusVariants } from '@/config/commercial-status';
import { shortDate } from '@/lib/format';

export function ProposalsPage() {
  const navigate = useNavigate();
  const q = useProposals();
  const nameMap = useCustomerNameMap();
  const [search, setSearch] = useState('');
  const rows = q.data ?? [];
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((x) => x.proposalNumber.toLowerCase().includes(s) || (nameMap.get(x.customerId) ?? '').toLowerCase().includes(s));
  }, [rows, search, nameMap]);

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Commercial" title="Proposals" description="Scope-of-work proposals — send, review, accept, and convert." />
      <DashboardCard flushBody>
        <div className="border-b border-border p-3">
          <FilterBar searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search proposals…" />
        </div>
        <QueryStateGuard data={filtered} isLoading={q.isLoading} isError={q.isError} emptyTitle="No proposals yet">
          {(data) => (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Number</TableHead><TableHead>Customer</TableHead>
                <TableHead>Scope</TableHead><TableHead>Status</TableHead><TableHead>Valid until</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.map((x) => (
                  <TableRow key={x.id} className="cursor-pointer" onClick={() => navigate(`/proposals/${x.id}`)}>
                    <TableCell className="font-mono text-[12px]">{x.proposalNumber}</TableCell>
                    <TableCell className="font-medium">{nameMap.get(x.customerId) ?? x.customerId.slice(0, 8)}</TableCell>
                    <TableCell className="max-w-[280px] truncate text-muted-foreground">{x.scopeSummary ?? '—'}</TableCell>
                    <TableCell><StatusBadge status={x.status} variants={proposalStatusVariants} /></TableCell>
                    <TableCell className="text-muted-foreground">{x.validUntil ? shortDate(x.validUntil) : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </QueryStateGuard>
      </DashboardCard>
    </div>
  );
}
